import React from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert
} from '@mui/material';
import {
  Send as SendIcon,
  Download as DownloadIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { openAirtableForm, downloadPDFForm } from '../utils/formHelpers';

const SubmitCase = () => {
  const handleFormSubmit = () => {
    openAirtableForm();
  };

  const handleDownloadForm = () => {
    downloadPDFForm();
  };

  const misconductExamples = [
    'Concealing exculpatory evidence',
    'Ignoring information',
    'Deception at trial',
    'Perjury by police officers',
    'Misstatements by prosecutors',
    'Evidence tampering',
    'Fabrication of evidence',
    'Forensic fraud',
    'Fake crimes',
    'Fictitious confessions',
    'Improper interrogations',
    'Coercion of witnesses',
    'Coercion of defendants',
    'Accepting bribes',
    'Racial profiling',
    'Conflict of interest',
    'Violation of procedural rules',
    'Violation of Rules of Professional Conduct',
    'Inappropriate remarks/courtroom behavior'
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Request a Questionnaire
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '800px', mx: 'auto' }}>
          Start the process to request our detailed questionnaire for yourself or a loved one who has 
          experienced misconduct in their case.
        </Typography>
      </Box>

      {/* Important Notes - Moved to top */}
      <Alert severity="warning" sx={{ mb: 6 }}>
        <Typography variant="body1">
          <strong>Important:</strong> This initial form collects basic information and communication 
          preferences. After submission, we will follow up with the detailed questionnaire based on 
          the preferred contact method you provide.
        </Typography>
      </Alert>

      {/* Main Action Cards */}
      <Grid container spacing={4} sx={{ mb: 6 }}>
        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <Card 
            sx={{ 
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              border: '2px solid',
              borderColor: 'primary.main',
              borderColor: 'primary.main',
              '&:hover': { 
                borderColor: 'primary.dark',
                transform: 'translateY(-2px)',
                transition: 'all 0.3s ease'
              }
            }}
          >
            <CardContent sx={{ p: 4, textAlign: 'center', flexGrow: 1 }}>
              <SendIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Request a Questionnaire Form
              </Typography>
              <Typography variant="body2" gutterBottom sx={{ fontStyle: 'italic', mb: 2 }}>
                (for yourself or a loved one)
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Please complete this form to provide basic information about the person who has suffered 
                misconduct in their case and learn about their communication preferences. We will then reach 
                out to provide that person (or their loved one/point of contact) with our questionnaire to 
                start gathering more specific data about the case.
              </Typography>
                            <Button
                variant="contained"
                size="large"
                onClick={handleFormSubmit}
                startIcon={<SendIcon />}
                sx={{ 
                  py: 1.5, 
                  px: 4,
                  fontSize: '1.1rem',
                  fontWeight: 600
                }}
              >
                Request Questionnaire
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <Card 
            sx={{ 
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              border: '2px solid',
              borderColor: 'grey.300',
              '&:hover': { 
                borderColor: 'grey.400',
                transform: 'translateY(-2px)',
                transition: 'all 0.3s ease'
              }
            }}
          >
            {/* <CardContent sx={{ p: 4, textAlign: 'center', flexGrow: 1 }}>
              <DownloadIcon sx={{ fontSize: 48, color: 'grey.600', mb: 2 }} />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Download Physical Form
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Download a PDF version of our questionnaire to complete by hand
                and mail to our office.
              </Typography>
              <Button
                variant="outlined"
                size="large"
                onClick={handleDownloadForm}
                startIcon={<DownloadIcon />}
                sx={{ 
                  py: 1.5, 
                  px: 4,
                  fontSize: '1.1rem',
                  fontWeight: 600
                }}
              >
                Download PDF
              </Button>
            </CardContent> */}
          </Card>
        </Grid>
      </Grid>

      {/* What Qualifies as Misconduct */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, textAlign: 'center', mb: 4 }}>
          What Qualifies as Misconduct?
        </Typography>
        
        <Alert severity="info" sx={{ mb: 4 }}>
          <Typography variant="body1">
            <strong>"Misconduct"</strong> refers to situations where an official actor abused their authority 
            in a specific case and violated people's constitutional rights, state statutes, and/or rules 
            of professional conduct. We focus on cases involving police officers, prosecutors, judges, 
            expert witnesses, and other official actors.
          </Typography>
        </Alert>

        <Alert severity="warning" sx={{ mb: 4 }}>
          <Typography variant="body1">
            <strong>Important:</strong> Reports of misconduct will ONLY be made publicly available if we find external sources of corroboration (i.e., legal filings, newspaper articles, etc.).
          </Typography>
        </Alert>

        <Card>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Examples of Misconduct include:
            </Typography>
            <Grid container spacing={1}>
              {misconductExamples.map((example, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Chip
                    label={example}
                    variant="outlined"
                    size="small"
                    sx={{ 
                      m: 0.5,
                      width: '100%',
                      justifyContent: 'flex-start',
                      '& .MuiChip-label': {
                        whiteSpace: 'normal',
                        textAlign: 'left'
                      }
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {/* Process Information */}
      <Card sx={{ mb: 6 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            What Happens After You Submit?
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Review Process"
                secondary="Our team will review your submission and may reach out for additional details."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Verification"
                secondary="We verify claims using news articles, legal documents, and other public resources."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Privacy Protection"
                secondary="All responses are kept private during verification. Only verified cases may be added to our public database."
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Disclaimer */}
      <Card sx={{ backgroundColor: 'grey.50' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Disclaimer
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please note that we are not a legal services organization and we do not represent people 
            ourselves or refer them to attorneys. Instead, we gather data about official misconduct 
            in the hopes that our larger database will help to identify previously unknown patterns 
            of abuse that will eventually allow some people affected by misconduct to achieve greater justice.
          </Typography>
        </CardContent>
      </Card>
    </Container>
  );
};

export default SubmitCase;
